import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { Trophy, Flame, Store, ArrowRight } from "lucide-react";

/**
 * Rotating "Top Vendor of the Week" spotlight. Reads from
 * GET /api/discover/top-vendors-of-week which auto-rotates at UTC Monday
 * via a `week_bucket` cache key. Non-destructive: if the endpoint returns
 * zero items the component renders nothing so the surface never regresses.
 */
export default function TopVendorSpotlight({ limit = 3, variant = "card" }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [bucket, setBucket] = useState(null);

  useEffect(() => {
    api
      .get("/discover/top-vendors-of-week", { params: { limit } })
      .then(({ data }) => {
        setItems(data.items || []);
        setBucket(data.week_bucket);
      })
      .catch(() => {});
  }, [limit]);

  if (!items.length) return null;

  if (variant === "banner") {
    const top = items[0];
    return (
      <button
        onClick={() => navigate(`/shop/${top.shop_id}`)}
        className="w-full rounded-2xl border border-amber-400/40 bg-gradient-to-r from-amber-500/15 via-pink-500/10 to-cyan-500/15 p-3 flex items-center gap-3 hover:border-amber-400/70 transition-colors text-left"
        data-testid="top-vendor-banner"
      >
        <div className="w-12 h-12 rounded-2xl overflow-hidden bg-white/5 shrink-0">
          {top.logo_url ? <img src={fileUrl(top.logo_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><Store className="w-5 h-5 text-amber-300" /></div>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] font-black text-amber-300">
            <Trophy className="w-3 h-3" /> Top Vendor This Week
          </p>
          <p className="text-sm font-black truncate">{top.name}</p>
          <p className="text-[11px] text-zinc-400">{top.completed_orders_7d} completed orders · {top.review_count} reviews</p>
        </div>
        <ArrowRight className="w-4 h-4 text-amber-300 shrink-0" />
      </button>
    );
  }

  return (
    <section className="rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 via-zinc-950 to-pink-500/10 p-5 space-y-3" data-testid="top-vendor-spotlight">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-black tracking-tight inline-flex items-center gap-2 text-amber-100">
          <Trophy className="w-4 h-4 text-amber-300" /> Top vendors this week
        </h2>
        {bucket && <span className="text-[10px] text-zinc-500 uppercase tracking-widest">{bucket}</span>}
      </div>
      <div className="space-y-2">
        {items.map((v, i) => (
          <button
            key={v.shop_id}
            onClick={() => navigate(`/shop/${v.shop_id}`)}
            className="w-full flex items-center gap-3 rounded-2xl bg-white/[0.03] border border-white/10 p-3 hover:border-amber-400/50 transition-colors text-left"
            data-testid={`top-vendor-${i}`}
          >
            <div className="w-10 h-10 rounded-full bg-amber-400/10 border border-amber-400/40 grid place-items-center font-black text-amber-200 shrink-0">
              {i === 0 ? <Flame className="w-5 h-5 text-amber-300" /> : `#${i + 1}`}
            </div>
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/5 shrink-0">
              {v.logo_url ? <img src={fileUrl(v.logo_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><Store className="w-4 h-4 text-zinc-400" /></div>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black truncate">{v.name}</p>
              <p className="text-[11px] text-zinc-400 truncate">{v.completed_orders_7d} orders this week · {v.review_count} reviews{v.tagline ? ` · ${v.tagline}` : ""}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-zinc-500 shrink-0" />
          </button>
        ))}
      </div>
    </section>
  );
}
